import React, { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $insertNodes,
} from "lexical";
import { marked } from "marked";
import {
  formatLatexInHtmlTags,
  processLatex,
} from "../../../../../utils/PreProcessorLatexCode";
import {
  addLineBreaksAroundImages,
  normalizeLatexToDoubleDollar,
  toEmbedUrl,
} from "../../dashboard/teacher/lessons/NewLessons/utils/fileHelpers.js";
import { addBreakBeforeGraphImages } from "../../../../../utils/utlis.js";

/**
 * PastePlugin that handles conversion of plain text that should be HTML
 * Specifically designed for chat.answer content that's being pasted as text/plain
 */

// Attribute names we refuse to copy from the clipboard (event handlers etc.)
const SAFE_ATTR_NAME = /^[a-zA-Z][\w:-]*$/;

const extractSoloImage = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const imgs = doc.querySelectorAll("img");
    if (imgs.length !== 1) return null;
    if ((doc.body.textContent || "").trim()) return null; // has real text too

    const img = imgs[0];
    const src = img.getAttribute("src") || "";
    if (!/^https?:\/\//i.test(src)) return null; // ignore data:/blob:/file:

    // Carry through everything else on the tag (class, question-id, width, ...).
    const attrs = {};
    for (const { name, value } of Array.from(img.attributes)) {
      if (name === "src" || name === "alt") continue; // handled explicitly
      if (name.toLowerCase().startsWith("on")) continue; // no inline handlers
      if (!SAFE_ATTR_NAME.test(name)) continue;
      attrs[name] = value;
    }

    return { src, alt: img.getAttribute("alt") || "", attrs };
  } catch (err) {
    console.error("extractSoloImage failed:", err);
    return null;
  }
};

function PastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Function to handle paste events
    const handlePaste = (event) => {
      // Access the clipboard data
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      // Get all available content types
      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      // Skip if no content is available
      if ((!html || html.trim() === "") && (!text || text.trim() === "")) {
        return;
      }

      // Single-image paste (from the chat panel's copy button, or any page)
      const soloImage = html && html.trim() !== "" ? extractSoloImage(html) : null;
      if (soloImage) {
        event.preventDefault();
        event.stopPropagation();

        editor.update(() => {
          try {
            const escapeAttr = (v) =>
              String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;");

            const extraAttrs = Object.entries(soloImage.attrs || {})
              .map(([name, value]) => `${name}="${escapeAttr(value)}"`)
              .join(" ");

            const imgHtml =
              `<img src="${escapeAttr(soloImage.src)}"` +
              ` alt="${escapeAttr(soloImage.alt)}"` +
              (extraAttrs ? ` ${extraAttrs}` : "") +
              ` />`;

            const dom = new DOMParser().parseFromString(
              `<p class="editor-paragraph"><br></p>${imgHtml}`,
              "text/html",
            );
            const nodes = $generateNodesFromDOM(editor, dom);
            if (!nodes.length) {
              console.error("Pasted image produced no Lexical nodes");
              return;
            }
            $insertNodes(nodes);
          } catch (error) {
            console.error("Image paste insert failed:", error);
          }
        });
        return;
      }

      // If HTML is available, process it normally
      if (html && html.trim() !== "") {
        // Let another handler process this, or add your HTML handling here
        return false;
      }

      // If we only have plain text, check if it looks like HTML or markdown
      const looksLikeHtmlOrLatex =
        text.includes("<p>") ||
        text.includes("<br>") ||
        text.includes("<strong>") ||
        text.includes("$$");
      const looksLikeMarkdown =
        /^#+\s/m.test(text) ||
        /^\s*[-*]\s/m.test(text) ||
        text.includes("__") ||
        (text.includes("**") && !text.includes("<")) ||
        /^---\s*$/m.test(text);
      if (
        text &&
        text.trim() !== "" &&
        (looksLikeHtmlOrLatex || looksLikeMarkdown)
      ) {
        event.preventDefault();
        event.stopPropagation();

        // Process the text the same way as chatHist[index].answer in NewLessons (UpdatePlugin)
        editor.update(() => {
          const parser = new DOMParser();


          // ── extract YouTube URL from the first .youtube_timestamp button ──
          // The pasted text contains buttons like:
          // <button className="youtube_timestamp" data-url="https://youtu.be/...">00:00</button>
          // All buttons share the same base URL, so the first match is enough.
          let rawYtUrl = "";
          const dataUrlMatch = text.match(
            /<button[^>]*youtube_timestamp[^>]*data-url="([^"]+)"/i
          );
          if (dataUrlMatch) {
            // strip any ?t= / &t= timestamp so the iframe starts from the beginning
            rawYtUrl = dataUrlMatch[1].replace(/[?&]t=\d+$/i, "");
          }

          const iframe = rawYtUrl
            ? `<iframe width="560" height="315" src="${toEmbedUrl(rawYtUrl)}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`
            : "";

          const addNewLine = addLineBreaksAroundImages(text);

          const preprocessedText = normalizeLatexToDoubleDollar(addNewLine);

          const sections = preprocessedText?.split(/\$\$/) || [];

          const processedSections = sections.map((section, index) => {
            if (index % 2 === 1) {
              if (!section || section.trim() === "") {
                return "";
              }
              const escapedSection = section
                .replace(/</g, " \\lt ")
                .replace(/>/g, " \\gt ");

              return `<p>$$${escapedSection}$$</p>`;
            }

            return marked(section);
          });

          let last_chatHist = processedSections.join("");

          const finalAnswer = last_chatHist
            .replace("<STARTOFTURN>", "")
            .replace("<ENDOFTURN>", "");

          // Post-process to merge LaTeX spans that appear between paragraph tags into the paragraphs
          let mergedContent = finalAnswer
            .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<p>/gi, (match, latexContent) => {
              return `</p><p><span>${latexContent}</span>`;
            })
            .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*(<h[1-6]|<\/h[1-6]|<ul|<\/ul|<ol|<\/ol)/gi, (match, latexContent, nextTag) => {
              return `<p><span>${latexContent}</span></p>${nextTag}`;
            })
            .replace(/(<\/h[1-6]|<\/ul|<\/ol)\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<p>/gi, (match, closingTag, latexContent) => {
              return `${closingTag}<p><span>${latexContent}</span></p><p>`;
            })
            .replace(/<\/p>\s*<span>(\$\$[^$]*?\$\$)<\/span>\s*<\/p>/gi, (match, latexContent) => {
              return `<p><span>${latexContent}</span></p>`;
            });

          let processedContent = formatLatexInHtmlTags(mergedContent);


          // NEW: blank paragraph before every graph-image so it renders on its own line
          processedContent = addBreakBeforeGraphImages(processedContent);

          processedContent = iframe + processedContent;



          const dom = parser.parseFromString(processedContent, "text/html");

          let nodes = $generateNodesFromDOM(editor, dom);
          try {
            $insertNodes(nodes);
            const htmlString = $generateHtmlFromNodes(editor, null);
            let root = $getRoot();
            root.clear();
            const dom = parser.parseFromString(
              `${htmlString}<h1> </h1>`,
              "text/html"
            );
            nodes = $generateNodesFromDOM(editor, dom);
            $insertNodes(nodes);
          } catch (error) {
            console.error(error);
            const root = $getRoot();
            const p = $createParagraphNode();
            p.append($createTextNode(text));
            root.append(p);
          }
        });

        return true; // We handled the event
      }

      // Otherwise, let the default handler process it
      return false;
    };

    // Get the editor root element
    const rootElement = editor.getRootElement();
    if (!rootElement) {
      return;
    }

    // Attach the paste event handler
    rootElement.addEventListener("paste", handlePaste, {
      capture: true, // Use capture phase to intercept before bubbling
      passive: false, // Allow preventDefault
    });

    // Clean up when unmounting
    return () => {
      rootElement.removeEventListener("paste", handlePaste, {
        capture: true,
        passive: false,
      });
    };
  }, [editor]);

  return null; // This plugin doesn't render anything
}

export default PastePlugin;
